import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api-client.js';
import { formatDate } from '@cardealeros/shared';
import { useState } from 'react';

export default function AdminDashboardPage() {
  const [activeTab, setActiveTab] = useState<'overview' | 'dealerships' | 'analytics' | 'activity' | 'errors'>('overview');
  const [selectedDealership, setSelectedDealership] = useState<string | null>(null);

  const { data: statsRes } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: () => api.get<any>('/admin/stats'),
  });

  const { data: dealershipsRes } = useQuery({
    queryKey: ['admin-dealerships'],
    queryFn: () => api.get<any>('/admin/dealerships?limit=50'),
    enabled: activeTab === 'overview' || activeTab === 'dealerships',
  });

  const { data: activityRes } = useQuery({
    queryKey: ['admin-activity'],
    queryFn: () => api.get<any>('/admin/activity?limit=50'),
    enabled: activeTab === 'activity',
  });

  const { data: errorsRes } = useQuery({
    queryKey: ['admin-errors'],
    queryFn: () => api.get<any>('/admin/errors?limit=50'),
    enabled: activeTab === 'errors',
  });
const { data: visitorStatsRes } = useQuery({
  queryKey: ['visitor-stats'],
  queryFn: () => api.get<any>('/tracker/stats'),
  enabled: activeTab === 'analytics',
});
const { data: recentVisitsRes } = useQuery({
  queryKey: ['recent-visits'],
  queryFn: () => api.get<any>('/tracker/recent'),
  enabled: activeTab === 'analytics',
});

  const { data: detailRes } = useQuery({
    queryKey: ['admin-dealership-detail', selectedDealership],
    queryFn: () => api.get<any>(`/admin/dealerships/${selectedDealership}`),
    enabled: !!selectedDealership,
  });

  const stats = statsRes?.data;
  const dealerships = dealershipsRes?.data ?? [];
  const activity = activityRes?.data ?? [];
  const errors = errorsRes?.data ?? [];
  const detail = detailRes?.data;
const visitorStats = visitorStatsRes;
const recentVisits = recentVisitsRes ?? [];

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'dealerships', label: 'Dealerships' },
    { key: 'analytics', label: 'Analytics' },
    { key: 'activity', label: 'Activity' },
    { key: 'errors', label: 'Errors' },
  ] as const;

  return (
    <div className="page">
      <h2 className="text-2xl font-bold mb-1">System Admin</h2>
      <p className="text-surface-500 text-sm mb-6">AutoCarPost management console</p>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 overflow-x-auto -mx-4 px-4">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => { setActiveTab(tab.key); setSelectedDealership(null); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === tab.key
                ? 'bg-brand-600 text-white'
                : 'bg-surface-800 text-surface-400 hover:text-white'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && stats && (
        <>
          <div className="grid grid-cols-2 gap-3 mb-6">
            {[
              { label: 'Dealerships', value: stats.totals.dealerships, color: 'text-brand-400' },
              { label: 'Users', value: stats.totals.users, color: 'text-green-400' },
              { label: 'Vehicles', value: stats.totals.vehicles, color: 'text-yellow-400' },
              { label: 'Photos', value: stats.totals.photos, color: 'text-blue-400' },
              { label: '24h Activity', value: stats.totals.activity_24h, color: 'text-purple-400' },
            ].map(({ label, value, color }) => (
              <div key={label} className="card p-4">
                <p className="text-xs text-surface-500 mb-1">{label}</p>
                <p className={`text-2xl font-bold ${color}`}>{value?.toLocaleString() ?? 0}</p>
              </div>
            ))}
          </div>

          {/* Vehicles by Status */}
          {stats.vehicles_by_status?.length > 0 && (
            <div className="card p-4 mb-4">
              <h3 className="font-semibold text-sm mb-3">Vehicles by Status</h3>
              <div className="space-y-2">
                {stats.vehicles_by_status.map((s: any) => (
                  <div key={s.status} className="flex justify-between text-sm">
                    <span className="text-surface-400 capitalize">{s.status}</span>
                    <span className="font-medium">{s.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Dealerships by Tier */}
          {stats.dealerships_by_tier?.length > 0 && (
            <div className="card p-4 mb-4">
              <h3 className="font-semibold text-sm mb-3">Dealerships by Tier</h3>
              <div className="space-y-2">
                {stats.dealerships_by_tier.map((t: any) => (
                  <div key={t.tier} className="flex justify-between text-sm">
                    <span className="text-surface-400 capitalize">{t.tier}</span>
                    <span className="font-medium">{t.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Dealerships Tab */}
      {activeTab === 'dealerships' && !selectedDealership && (
        <div className="space-y-3">
          {dealerships.map((d: any) => (
            <button
              key={d.id}
              onClick={() => setSelectedDealership(d.id)}
              className="card p-4 w-full text-left hover:border-brand-500 transition-colors"
            >
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-semibold">{d.name}</p>
                  <p className="text-surface-500 text-xs mt-1">{d.email}</p>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  d.tier === 'enterprise' ? 'bg-purple-900/50 text-purple-400' :
                  d.tier === 'pro' ? 'bg-brand-900/50 text-brand-400' :
                  d.tier === 'starter' ? 'bg-green-900/50 text-green-400' :
                  'bg-surface-700 text-surface-400'
                }`}>{d.tier}</span>
              </div>
              <div className="flex gap-4 mt-2 text-xs text-surface-500">
                <span>{d.user_count} users</span>
                <span>{d.vehicle_count} vehicles</span>
                <span>{d.subscription_status ?? 'none'}</span>
                <span>{formatDate(d.created_at)}</span>
              </div>
            </button>
          ))}
          {dealerships.length === 0 && (
            <p className="text-surface-500 text-center py-10">No dealerships yet</p>
          )}
        </div>
      )}

      {/* Dealership Detail */}
      {activeTab === 'dealerships' && selectedDealership && detail && (
        <div>
          <button
            onClick={() => setSelectedDealership(null)}
            className="btn-ghost mb-4 -ml-2"
          >&larr; All Dealerships</button>

          <div className="card p-4 mb-4">
            <h3 className="font-semibold text-lg">{detail.name}</h3>
            <p className="text-surface-500 text-sm">{detail.email}</p>
            <div className="grid grid-cols-2 gap-3 mt-4">
              {[
                { label: 'Tier', value: detail.tier },
                { label: 'Status', value: detail.subscription_status ?? 'none' },
                { label: 'License', value: detail.license_number ?? '—' },
                { label: 'Joined', value: formatDate(detail.created_at) },
                { label: 'City', value: [detail.city, detail.state].filter(Boolean).join(', ') || '—' },
                { label: 'Phone', value: detail.phone ?? '—' },
              ].map(({ label, value }) => (
                <div key={label}>
                  <p className="text-xs text-surface-500">{label}</p>
                  <p className="text-sm font-medium capitalize">{value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Staff */}
          {detail.users?.length > 0 && (
            <div className="card p-4 mb-4">
              <h3 className="font-semibold text-sm mb-3">Staff ({detail.users.length})</h3>
              <div className="space-y-2">
                {detail.users.map((u: any) => (
                  <div key={u.id} className="flex justify-between items-center text-sm">
                    <div>
                      <span className="font-medium">{u.name}</span>
                      <span className="text-surface-500 ml-2">{u.email}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-surface-700 rounded text-xs capitalize">{u.role}</span>
                      <span className={`w-2 h-2 rounded-full ${u.is_active ? 'bg-green-400' : 'bg-red-400'}`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Vehicles */}
          {detail.recent_vehicles?.length > 0 && (
            <div className="card p-4 mb-4">
              <h3 className="font-semibold text-sm mb-3">Recent Vehicles</h3>
              <div className="space-y-2">
                {detail.recent_vehicles.map((v: any) => (
                  <div key={v.id} className="flex justify-between items-center text-sm">
                    <span>{[v.year, v.make, v.model].filter(Boolean).join(' ') || v.vin}</span>
                    <span className="text-surface-500 text-xs capitalize">{v.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}


{/* Analytics Tab */}
{activeTab === 'analytics' && (
<div className="space-y-6">
  {visitorStats?.totals && (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <div className="card p-4 text-center">
        <div className="text-2xl font-bold">{visitorStats.totals.total}</div>
        <div className="text-xs text-secondary">Total Views</div>
      </div>
      <div className="card p-4 text-center">
        <div className="text-2xl font-bold">{visitorStats.totals.unique_visitors}</div>
        <div className="text-xs text-secondary">Unique Visitors</div>
      </div>
      <div className="card p-4 text-center">
        <div className="text-2xl font-bold">{visitorStats.totals.humans}</div>
        <div className="text-xs text-secondary">Human Views</div>
      </div>
      <div className="card p-4 text-center">
        <div className="text-2xl font-bold">{visitorStats.totals.bots}</div>
        <div className="text-xs text-secondary">Bots</div>
      </div>
    </div>
  )}

  <div className="grid md:grid-cols-2 gap-4">
    {visitorStats?.byPath?.length > 0 && (
      <div className="card p-4">
        <h3 className="font-semibold mb-2">Top Pages</h3>
        <div className="space-y-1 text-sm">
          {visitorStats.byPath.map((p: any, i: number) => (
            <div key={i} className="flex justify-between">
              <span className="truncate mr-2">{p.path}</span>
              <span className="text-secondary whitespace-nowrap">{p.views}</span>
            </div>
          ))}
        </div>
      </div>
    )}

    {visitorStats?.topReferrers?.length > 0 && (
      <div className="card p-4">
        <h3 className="font-semibold mb-2">Top Referrers</h3>
        <div className="space-y-1 text-sm">
          {visitorStats.topReferrers.map((r: any, i: number) => (
            <div key={i} className="flex justify-between">
              <span className="truncate mr-2">{r.referrer || '(direct)'}</span>
              <span className="text-secondary whitespace-nowrap">{r.views}</span>
            </div>
          ))}
        </div>
      </div>
    )}

    {visitorStats?.byDevice?.length > 0 && (
      <div className="card p-4">
        <h3 className="font-semibold mb-2">Devices</h3>
        <div className="space-y-1 text-sm">
          {visitorStats.byDevice.map((d: any, i: number) => (
            <div key={i} className="flex justify-between">
              <span>{d.device_type || 'unknown'}</span>
              <span className="text-secondary">{d.views}</span>
            </div>
          ))}
        </div>
      </div>
    )}

    {visitorStats?.byCountry?.length > 0 && (
      <div className="card p-4">
        <h3 className="font-semibold mb-2">Countries</h3>
        <div className="space-y-1 text-sm">
          {visitorStats.byCountry.slice(0, 10).map((c: any, i: number) => (
            <div key={i} className="flex justify-between">
              <span>{c.country || 'unknown'}</span>
              <span className="text-secondary">{c.views}</span>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>

  {recentVisits.length > 0 && (
    <div className="card p-4">
      <h3 className="font-semibold mb-2">Recent Visits</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-secondary">
              <th className="pb-1">Path</th>
              <th className="pb-1">Device</th>
              <th className="pb-1">Country</th>
              <th className="pb-1">Time</th>
            </tr>
          </thead>
          <tbody>
            {recentVisits.slice(0, 30).map((v: any, i: number) => (
              <tr key={i} className="border-t border-border">
                <td className="py-1 truncate max-w-[200px]">{v.path}</td>
                <td className="py-1">{v.device_type}</td>
                <td className="py-1">{v.country || '-'}</td>
                <td className="py-1 whitespace-nowrap">{new Date(v.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )}
</div>
)}
      {/* Activity Tab */}
      {activeTab === 'activity' && (
        <div className="space-y-2">
          {activity.map((a: any, i: number) => (
            <div key={i} className="card p-3">
              <div className="flex justify-between items-start">
                <span className="text-sm font-medium">{a.action}</span>
                <span className="text-xs text-surface-500">{formatDate(a.created_at)}</span>
              </div>
              {a.details && (
                <p className="text-xs text-surface-500 mt-1 truncate">{a.details}</p>
              )}
            </div>
          ))}
          {activity.length === 0 && (
            <p className="text-surface-500 text-center py-10">No activity yet</p>
          )}
        </div>
      )}

      {/* Errors Tab */}
      {activeTab === 'errors' && (
        <div className="space-y-2">
          {errors.map((e: any, i: number) => (
            <div key={i} className="card p-3 border-red-900/30">
              <div className="flex justify-between items-start">
                <span className="text-sm font-medium text-red-400">{e.action}</span>
                <span className="text-xs text-surface-500">{formatDate(e.created_at)}</span>
              </div>
              {e.details && (
                <p className="text-xs text-surface-400 mt-1">{e.details}</p>
              )}
              {e.dealership_id && (
                <p className="text-xs text-surface-600 mt-1">Dealership: {e.dealership_id}</p>
              )}
            </div>
          ))}
          {errors.length === 0 && (
            <p className="text-green-400 text-center py-10">No errors found</p>
          )}
        </div>
      )}
    </div>
  );
}
